import {
  ActivityInterface,
  CrmObjectInterface,
  FieldSchemaInterface,
  FieldType as LegacyFieldType,
  FieldValueInterface,
  FilterGroupInterface,
  FilterInterface,
  FilterValueInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldSchemaResponseInterface,
  NullValue as LegacyNullValue,
  ObjectProjectionFilterInterface,
  PagedResponseMetadataInterface,
  SortByInterface,
} from '@vendasta/crm';
import { FieldTypeValue, ProjectionFilterTypeValue, SortDirectionValue } from '@vendasta/protos/crm/v1/enums';
import type {
  Activity,
  CrmObject,
  FieldSchema,
  FieldValue,
  GetMultiActivityResponse,
  GetMultiCrmObjectRequest,
  GetMultiCrmObjectResponse,
  ListActivitiesRequest,
  ListActivitiesResponse,
  ListCrmObjectsRequest,
  ListCrmObjectsResponse,
  ListFieldSchemaResponse,
  ObjectProjectionFilter,
  SortBy,
  Timer,
} from '@vendasta/protos/crm/v1/types';
import { DateDefaultValue, FilterGroupOperatorValue, FilterOperatorValue } from '@vendasta/protos/galaxytypes/v1/enums';
import type { Filter, FilterGroup, FilterValue } from '@vendasta/protos/galaxytypes/v1/types';
import type { DeepPartial } from '@vendasta/protos/transport';
import type { PagedResponseMetadata } from '@vendasta/protos/vendastatypes/types';

/**
 * Temporary functions that translate from `@vendasta/protos/crm/v1` responses to the `@vendasta/crm`
 * interfaces the rest of the CRM lib still consumes. Requests only need a type cast
 * because both SDKs put the same JSON. Responses need string enums mapped
 * back to the legacy numeric enums and int64 strings coerced to numbers.
 *
 * Delete once all consumers read proto types directly.
 */

export type FieldValueWithTimer = FieldValueInterface & { timerValue?: Timer };

export function isTimerFieldType(fieldType: LegacyFieldType | undefined): boolean {
  return Number(fieldType) === FieldTypeValue.FIELD_TYPE_TIMER;
}

function toProtoEnum<K extends string>(valueMap: Record<K, number>, value: number | undefined): K | undefined {
  if (value === undefined) {
    return undefined;
  }
  return (Object.keys(valueMap) as K[]).find((key) => valueMap[key] === value);
}

function toProtoSortBy(sortBy: SortByInterface): DeepPartial<SortBy> {
  return { ...sortBy, sortDirection: toProtoEnum(SortDirectionValue, sortBy.sortDirection) };
}

function toProtoFilterValue(value: FilterValueInterface): DeepPartial<FilterValue> {
  return { ...value, dateDefault: toProtoEnum(DateDefaultValue, value.dateDefault) };
}

function toProtoFilter(filter: FilterInterface): DeepPartial<Filter> {
  return {
    ...filter,
    operator: toProtoEnum(FilterOperatorValue, filter.operator),
    values: filter.values?.map(toProtoFilterValue),
  };
}

function toProtoFilterGroup(group: FilterGroupInterface): DeepPartial<FilterGroup> {
  return {
    ...group,
    operator: toProtoEnum(FilterGroupOperatorValue, group.operator),
    filters: group.filters?.map(toProtoFilter),
  };
}

function toProtoProjectionFilter(filter: ObjectProjectionFilterInterface): DeepPartial<ObjectProjectionFilter> {
  return {
    ...filter,
    filterType: toProtoEnum(ProjectionFilterTypeValue, filter.filterType),
    fromFieldType: toProtoEnum(FieldTypeValue, filter.fromFieldType),
    toFieldType: toProtoEnum(FieldTypeValue, filter.toFieldType),
  };
}

export function toProtoListRequest(request: ListCrmObjectsRequestInterface): DeepPartial<ListCrmObjectsRequest> {
  return {
    ...request,
    sortBy: request.sortBy?.map(toProtoSortBy),
    filtersV2: request.filtersV2 ? toProtoFilterGroup(request.filtersV2) : undefined,
    projectionFilters: request.projectionFilters?.map(toProtoProjectionFilter),
  };
}

export function toProtoListActivitiesRequest(
  request: ListCrmObjectsRequestInterface,
): DeepPartial<ListActivitiesRequest> {
  return {
    namespace: request.namespace,
    pagingOptions: request.pagingOptions,
    search: request.search,
    sortBy: request.sortBy?.map(toProtoSortBy),
    filtersV2: request.filtersV2 ? toProtoFilterGroup(request.filtersV2) : undefined,
  };
}

export function toProtoGetMultiRequest(
  request: GetMultiCrmObjectRequestInterface,
): DeepPartial<GetMultiCrmObjectRequest> {
  return { ...request, projectionFilters: request.projectionFilters?.map(toProtoProjectionFilter) };
}

export function toLegacyTimer(timer: Timer): Timer {
  return { ...timer, duration: Number(timer.duration) };
}

export function toLegacyFieldValue(field: FieldValue): FieldValueWithTimer {
  const legacy: { -readonly [K in keyof FieldValue]?: FieldValue[K] } = { ...field };
  delete legacy.value;
  return {
    ...legacy,
    nullValue: field.nullValue === undefined ? undefined : LegacyNullValue.NULL_VALUE,
    integerValue: field.integerValue === undefined ? undefined : Number(field.integerValue),
    timerValue: field.timerValue === undefined ? undefined : toLegacyTimer(field.timerValue),
  };
}

export function toLegacyCrmObject(crmObject: CrmObject): CrmObjectInterface {
  return { ...crmObject, fields: crmObject.fields.map(toLegacyFieldValue) };
}

export function toLegacyActivity(activity: Activity): ActivityInterface {
  return { ...activity, fields: activity.fields.map(toLegacyFieldValue) };
}

export function toLegacyFieldSchema(schema: FieldSchema): FieldSchemaInterface {
  return { ...schema, fieldType: FieldTypeValue[schema.fieldType] as LegacyFieldType };
}

export function toLegacyPagingMetadata(
  metadata: PagedResponseMetadata | undefined,
): PagedResponseMetadataInterface | undefined {
  if (!metadata) {
    return undefined;
  }
  return { ...metadata, totalResults: Number(metadata.totalResults) };
}

export function toLegacyListResponse(response: ListCrmObjectsResponse): ListCrmObjectsResponseInterface {
  return {
    crmObjects: response.crmObjects.map(toLegacyCrmObject),
    pagingMetadata: toLegacyPagingMetadata(response.pagingMetadata),
  };
}

export function toLegacyListActivitiesResponse(response: ListActivitiesResponse): ListCrmObjectsResponseInterface {
  return {
    crmObjects: response.activities.map(toLegacyActivity),
    pagingMetadata: toLegacyPagingMetadata(response.pagingMetadata) ?? {},
  };
}

export function toLegacyGetMultiResponse(response: GetMultiCrmObjectResponse): GetMultiCrmObjectResponseInterface {
  return {
    crmObjects: response.crmObjects.map(toLegacyCrmObject),
    fieldSchemas: response.fieldSchemas.map(toLegacyFieldSchema),
  };
}

export function toLegacyGetMultiActivitiesResponse(
  response: GetMultiActivityResponse,
): GetMultiCrmObjectResponseInterface {
  return { crmObjects: response.activities.map(toLegacyActivity) };
}

export function toLegacyListFieldSchemaResponse(response: ListFieldSchemaResponse): ListFieldSchemaResponseInterface {
  return {
    fieldSchemas: response.fieldSchemas.map(toLegacyFieldSchema),
    pagingMetadata: toLegacyPagingMetadata(response.pagingMetadata),
  };
}
